import React, { useEffect, useState } from 'react';
import axiosInstance from '../api/axiosInstance';
import { useAuth } from '../hooks/useAuth';
import { getPendingRecommends, approveRecommend, rejectRecommend, RecommendedTimeline } from '../api/recommendApi';
import {
  CreditRequest,
  getPendingCreditRequests,
  grantCreditRequest,
  rejectCreditRequest
} from '../api/creditApi';
import './AdminCommon.css';

type AdminTab = 'users' | 'credits' | 'recommends' | 'system';

interface UserItem {
  id: number;
  username: string;
  email: string;
  nickname?: string;
  created_at?: string;
  roles?: string[];
  role?: string;
}

const AdminPage: React.FC = () => {
  const { isAdmin, loading } = useAuth();
  const [activeTab, setActiveTab] = useState<AdminTab>('users');

  const [users, setUsers] = useState<UserItem[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [migrations, setMigrations] = useState<any[]>([]);
  const [pendingRecommends, setPendingRecommends] = useState<RecommendedTimeline[]>([]);
  const [creditRequests, setCreditRequests] = useState<CreditRequest[]>([]);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);

  // 사용자 추가 모달 State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    username: '',
    email: '',
    password: '',
    nickname: '',
    roleName: 'ROLE_USER'
  });

  // 사용자 정보 수정 모달 State
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    id: 0,
    username: '',
    email: '',
    nickname: ''
  });

  // 비밀번호 변경 모달 State
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    id: 0,
    username: '',
    password: ''
  });

  useEffect(() => {
    if (!loading && isAdmin()) {
      fetchData();
    }
  }, [loading]);

  const fetchMigrations = async () => {
    try {
      const res = await axiosInstance.get('/api/admin/migrations');
      setMigrations(res.data);
    } catch (err) {
      console.error('마이그레이션 로딩 실패:', err);
    }
  };

  const fetchPendingRecommends = async () => {
    try {
      const data = await getPendingRecommends();
      setPendingRecommends(data);
    } catch (err) {
      console.error('추천 대기 신청 로딩 실패:', err);
    }
  };

  const fetchCreditRequests = async () => {
    try {
      const data = await getPendingCreditRequests();
      setCreditRequests(data);
    } catch (err) {
      console.error('크레딧 요청 목록 로딩 실패:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const usersRes = await axiosInstance.get('/api/users');
      setUsers(usersRes.data);
    } catch (err) {
      console.error('사용자 목록 로딩 실패:', err);
    }
  };

  const fetchData = async () => {
    try {
      await fetchUsers();
      const rolesRes = await axiosInstance.get('/api/roles');
      setRoles(rolesRes.data);
      await fetchMigrations();
      await fetchPendingRecommends();
      await fetchCreditRequests();
    } catch (err) {
      console.error('데이터 로딩 실패:', err);
    }
  };

  // 사용자 추가 처리
  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createForm.username || !createForm.email || !createForm.password) {
      setIsError(true);
      setMessage('아이디, 이메일, 비밀번호는 필수 입력 항목입니다.');
      return;
    }

    try {
      const res = await axiosInstance.post('/api/users', createForm);
      setIsError(false);
      setMessage(res.data.message || '사용자가 성공적으로 등록되었습니다.');
      setShowCreateModal(false);
      setCreateForm({ username: '', email: '', password: '', nickname: '', roleName: 'ROLE_USER' });
      await fetchUsers();
    } catch (err: any) {
      setIsError(true);
      setMessage('사용자 등록 실패: ' + (err.response?.data?.error || err.message));
    }
  };

  // 사용자 수정 모달 열기
  const openEditModal = (user: UserItem) => {
    setEditForm({
      id: user.id,
      username: user.username,
      email: user.email,
      nickname: user.nickname || ''
    });
    setShowEditModal(true);
  };

  // 사용자 정보 수정 처리
  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await axiosInstance.put(`/api/users/${editForm.id}`, {
        email: editForm.email,
        nickname: editForm.nickname
      });
      setIsError(false);
      setMessage(res.data.message || '사용자 정보가 수정되었습니다.');
      setShowEditModal(false);
      await fetchUsers();
    } catch (err: any) {
      setIsError(true);
      setMessage('사용자 정보 수정 실패: ' + (err.response?.data?.error || err.message));
    }
  };

  // 비밀번호 변경 모달 열기
  const openPasswordModal = (user: UserItem) => {
    setPasswordForm({
      id: user.id,
      username: user.username,
      password: ''
    });
    setShowPasswordModal(true);
  };

  // 비밀번호 변경 처리
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordForm.password || passwordForm.password.length < 6) {
      setIsError(true);
      setMessage('비밀번호는 최소 6자 이상이어야 합니다.');
      return;
    }

    try {
      const res = await axiosInstance.put(`/api/users/${passwordForm.id}/password`, {
        password: passwordForm.password
      });
      setIsError(false);
      setMessage(res.data.message || '비밀번호가 성공적으로 변경되었습니다.');
      setShowPasswordModal(false);
      setPasswordForm({ id: 0, username: '', password: '' });
    } catch (err: any) {
      setIsError(true);
      setMessage('비밀번호 변경 실패: ' + (err.response?.data?.error || err.message));
    }
  };

  // 역할 할당
  const handleAssignRole = async (userId: number, roleName: string) => {
    try {
      await axiosInstance.post('/api/roles/assign', { userId, roleName });
      setIsError(false);
      setMessage(`[${roleName}] 역할이 성공적으로 할당되었습니다.`);
      await fetchUsers();
    } catch (err: any) {
      setIsError(true);
      setMessage('역할 할당 실패: ' + (err.response?.data?.error || err.message));
    }
  };

  // 역할 제거
  const handleRemoveRole = async (userId: number, roleName: string) => {
    try {
      await axiosInstance.delete('/api/roles/remove', { data: { userId, roleName } });
      setIsError(false);
      setMessage(`[${roleName}] 역할이 성공적으로 제거되었습니다.`);
      await fetchUsers();
    } catch (err: any) {
      setIsError(true);
      setMessage('역할 제거 실패: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleRunMigrations = async () => {
    try {
      const res = await axiosInstance.post('/api/admin/migrations/run');
      setIsError(false);
      setMessage(res.data.message);
      await fetchMigrations();
    } catch (err: any) {
      setIsError(true);
      setMessage('마이그레이션 실행 실패: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleInitializeRoles = async () => {
    try {
      const res = await axiosInstance.post('/api/roles/initialize');
      setIsError(false);
      setMessage(res.data.message);
      await fetchData();
    } catch (err: any) {
      setIsError(true);
      setMessage('역할 초기화 실패: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleInitializeTables = async () => {
    try {
      const res = await axiosInstance.post('/api/admin/init-tables');
      setIsError(false);
      setMessage(res.data.message);
    } catch (err: any) {
      setIsError(true);
      setMessage('테이블 초기화 실패: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleApproveRecommend = async (id: number) => {
    try {
      await approveRecommend(id);
      setIsError(false);
      setMessage('추천 신청이 성공적으로 승인되었습니다.');
      await fetchPendingRecommends();
    } catch (err: any) {
      setIsError(true);
      setMessage('추천 승인 실패: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleRejectRecommend = async (id: number) => {
    if (!window.confirm('정말 추천 신청을 거절하시겠습니까?')) return;
    try {
      await rejectRecommend(id);
      setIsError(false);
      setMessage('추천 신청이 거절되었습니다.');
      await fetchPendingRecommends();
    } catch (err: any) {
      setIsError(true);
      setMessage('추천 거절 실패: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleGrantCredit = async (requestId: number, amount: number) => {
    try {
      await grantCreditRequest(requestId, amount);
      setIsError(false);
      setMessage(`${amount.toLocaleString()}C를 충전했습니다.`);
      await fetchCreditRequests();
    } catch (err: any) {
      setIsError(true);
      setMessage('크레딧 충전 실패: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleManualGrantCredit = async (requestId: number) => {
    const value = window.prompt('충전할 크레딧(C)을 입력하세요.');
    if (!value) return;
    const amount = Number(value);
    if (!Number.isInteger(amount) || amount < 1) {
      setIsError(true);
      setMessage('수동 입력 크레딧은 1 이상의 정수여야 합니다.');
      return;
    }
    await handleGrantCredit(requestId, amount);
  };

  const handleRejectCredit = async (requestId: number) => {
    if (!window.confirm('이 크레딧 요청을 반려하시겠습니까?')) return;
    try {
      await rejectCreditRequest(requestId);
      setIsError(false);
      setMessage('크레딧 요청을 반려했습니다.');
      await fetchCreditRequests();
    } catch (err: any) {
      setIsError(true);
      setMessage('크레딧 요청 반려 실패: ' + (err.response?.data?.error || err.message));
    }
  };

  if (loading) {
    return (
      <div className="admin-page-wrapper" style={{ textAlign: 'center', padding: '48px 0' }}>
        <div style={{ fontWeight: 600, fontSize: '16px', color: 'var(--admin-text-muted)' }}>
          관리자 권한 및 계정 정보를 확인 중입니다...
        </div>
      </div>
    );
  }

  if (!isAdmin()) {
    return (
      <div className="admin-page-wrapper" style={{ textAlign: 'center', padding: '48px 0' }}>
        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="var(--admin-danger)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '16px' }}>
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
        </svg>
        <h2 className="admin-page-title" style={{ color: 'var(--admin-danger)', marginBottom: '8px' }}>접근 권한이 없습니다</h2>
        <p style={{ color: 'var(--admin-text-muted)', margin: 0 }}>이 페이지는 시스템 관리자 전용 영역입니다.</p>
      </div>
    );
  }

  // 역할 뱃지 클래스 매핑 헬퍼
  const getRoleBadgeClass = (roleName: string) => {
    if (!roleName) return 'admin-badge-secondary';
    if (roleName.includes('ADMIN')) return 'admin-badge-danger';
    if (roleName.includes('EDITOR')) return 'admin-badge-primary';
    return 'admin-badge-success';
  };

  const getRecommendBadgeClass = (status: RecommendedTimeline['status']) => {
    if (status === 'approved') return 'admin-badge-success';
    if (status === 'rejected') return 'admin-badge-danger';
    return 'admin-badge-warning';
  };

  return (
    <div className="admin-page-wrapper">
      <div className="admin-page-header">
        <div>
          <h2 className="admin-page-title">운영자 대시보드</h2>
          <div className="admin-page-subtitle">역사 아카이브 시스템의 권한, 크레딧, 콘텐츠 승인 및 시스템 설정을 관리합니다.</div>
        </div>
      </div>
      
      {message && (
        <div className={`admin-alert ${isError ? 'admin-alert-danger' : 'admin-alert-success'}`}>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {isError ? (
              <>
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </>
            ) : (
              <polyline points="20 6 9 17 4 12"></polyline>
            )}
          </svg>
          {message}
        </div>
      )}

      {/* 상단 기능별 탭 메뉴 Navigation */}
      <div className="admin-tabs-container">
        <button
          type="button"
          className={`admin-tab-btn ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
          사용자 & 권한 관리
          <span className="admin-tab-badge">{users.length}</span>
        </button>

        <button
          type="button"
          className={`admin-tab-btn ${activeTab === 'credits' ? 'active' : ''}`}
          onClick={() => setActiveTab('credits')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="4" width="20" height="16" rx="2"></rect>
            <line x1="12" y1="8" x2="12" y2="16"></line>
            <line x1="8" y1="12" x2="16" y2="12"></line>
          </svg>
          크레딧(C) 관리
          <span className={`admin-tab-badge ${creditRequests.length > 0 ? 'badge-warning' : ''}`}>
            {creditRequests.length}
          </span>
        </button>

        <button
          type="button"
          className={`admin-tab-btn ${activeTab === 'recommends' ? 'active' : ''}`}
          onClick={() => setActiveTab('recommends')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
          </svg>
          추천 타임라인 관리
          <span className="admin-tab-badge">{pendingRecommends.length}</span>
        </button>

        <button
          type="button"
          className={`admin-tab-btn ${activeTab === 'system' ? 'active' : ''}`}
          onClick={() => setActiveTab('system')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3"></circle>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
          </svg>
          시스템 & DB 관리
        </button>
      </div>

      {/* ===================================================================
          TAB 1: 사용자 & 권한 관리
         =================================================================== */}
      {activeTab === 'users' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <h3 className="admin-section-title" style={{ margin: 0 }}>사용자 목록 및 권한 설정</h3>
              <div style={{ fontSize: '13px', color: 'var(--admin-text-muted)', marginTop: '4px' }}>
                운영자를 추가하거나, 기존 사용자의 계정 정보를 수정 및 비밀번호 재설정하고 역할을 지정합니다.
              </div>
            </div>
            <button
              type="button"
              className="admin-btn admin-btn-primary"
              onClick={() => setShowCreateModal(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              신규 사용자 추가
            </button>
          </div>

          {users.length === 0 ? (
            <div className="admin-empty-state">
              <svg className="admin-empty-icon" xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <div style={{ fontWeight: 600, fontSize: '16px', color: 'var(--admin-text-main)', marginBottom: '4px' }}>등록된 사용자가 없습니다.</div>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>사용자 아이디 / 닉네임</th>
                    <th>이메일 주소</th>
                    <th>배정된 역할(Roles)</th>
                    <th>관리 및 변경</th>
                    <th>역할 가감/조정</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => {
                    const userRoleList = user.roles || (user.role ? [user.role] : ['ROLE_USER']);
                    return (
                      <tr key={user.id}>
                        <td>{user.id}</td>
                        <td>
                          <div style={{ fontWeight: 600 }}>{user.username}</div>
                          {user.nickname && <div style={{ fontSize: '12px', color: 'var(--admin-text-muted)' }}>({user.nickname})</div>}
                        </td>
                        <td>{user.email}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                            {userRoleList.map(r => (
                              <span key={r} className={`admin-badge ${getRoleBadgeClass(r)}`}>
                                {r.replace('ROLE_', '')}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                              type="button"
                              className="admin-btn admin-btn-secondary admin-btn-sm"
                              onClick={() => openEditModal(user)}
                              title="정보 수정"
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                            >
                              ✏️ 수정
                            </button>
                            <button
                              type="button"
                              className="admin-btn admin-btn-secondary admin-btn-sm"
                              onClick={() => openPasswordModal(user)}
                              title="비밀번호 변경"
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                            >
                              🔑 암호 변경
                            </button>
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {roles.map((role: any) => {
                              const hasRole = userRoleList.includes(role.name);
                              return (
                                <div key={role.name} style={{ display: 'flex', gap: '2px', border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden' }}>
                                  <button
                                    onClick={() => handleAssignRole(user.id, role.name)}
                                    className="admin-btn admin-btn-sm"
                                    style={{ 
                                      padding: '4px 8px', 
                                      backgroundColor: hasRole ? 'var(--admin-success)' : '#ffffff',
                                      color: hasRole ? '#ffffff' : 'var(--admin-success)',
                                      border: 'none',
                                      borderRadius: 0,
                                      fontSize: '11px',
                                      fontWeight: 600
                                    }}
                                    title={`${role.name} 역할 할당`}
                                    disabled={hasRole}
                                  >
                                    +{role.description || role.name.replace('ROLE_', '')}
                                  </button>
                                  <button
                                    onClick={() => handleRemoveRole(user.id, role.name)}
                                    className="admin-btn admin-btn-sm"
                                    style={{ 
                                      padding: '4px 8px', 
                                      backgroundColor: '#ffffff',
                                      color: 'var(--admin-danger)',
                                      border: 'none',
                                      borderLeft: '1px solid #e2e8f0',
                                      borderRadius: 0,
                                      fontSize: '11px',
                                      fontWeight: 600
                                    }}
                                    title={`${role.name} 역할 제거`}
                                    disabled={!hasRole}
                                  >
                                    -
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ===================================================================
          TAB 2: 크레딧 관리
         =================================================================== */}
      {activeTab === 'credits' && (
        <div>
          <h3 className="admin-section-title" style={{ margin: '0 0 16px 0' }}>
            크레딧(C) 관리 및 충전 요청 처리
          </h3>
          <div className="admin-credit-panel">
            <div className="admin-credit-panel-header">
              <div>
                <h4>크레딧 요청 대기 목록</h4>
                <p>1000C 미만 사용자 또는 부족 상태에서 요청한 회원에게 운영자가 크레딧을 충전합니다.</p>
              </div>
              <button type="button" className="admin-btn admin-btn-secondary admin-btn-sm" onClick={fetchCreditRequests}>
                새로고침
              </button>
            </div>

            {creditRequests.length === 0 ? (
              <div className="admin-empty-state compact">
                <div style={{ fontWeight: 600, fontSize: '16px', color: 'var(--admin-text-main)', marginBottom: '4px' }}>
                  대기 중인 크레딧 요청이 없습니다.
                </div>
                <div style={{ fontSize: '13px' }}>회원이 크레딧(C) 요청하기를 누르면 이 영역에 표시됩니다.</div>
              </div>
            ) : (
              <div className="table-wrapper" style={{ marginBottom: 0 }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>회원</th>
                      <th>이메일</th>
                      <th>요청 당시</th>
                      <th>현재 보유</th>
                      <th>요청 일시</th>
                      <th style={{ minWidth: '360px' }}>충전 처리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creditRequests.map(request => (
                      <tr key={request.id}>
                        <td>
                          <div className="admin-credit-user">
                            <strong>{request.nickname || request.username}</strong>
                            <span>ID {request.user_id} · {request.username}</span>
                          </div>
                        </td>
                        <td>{request.email}</td>
                        <td>{Number(request.balance_at_request || 0).toLocaleString()}C</td>
                        <td>
                          <span className={`admin-badge ${request.credit_balance < 1000 ? 'admin-badge-warning' : 'admin-badge-success'}`}>
                            {Number(request.credit_balance || 0).toLocaleString()}C
                          </span>
                        </td>
                        <td>{new Date(request.requested_at).toLocaleString()}</td>
                        <td>
                          <div className="admin-credit-actions">
                            {[1000, 3000, 5000, 10000].map(amount => (
                              <button
                                key={amount}
                                type="button"
                                className="admin-btn admin-btn-success admin-btn-sm"
                                onClick={() => handleGrantCredit(request.id, amount)}
                              >
                                {amount.toLocaleString()}C
                              </button>
                            ))}
                            <button
                              type="button"
                              className="admin-btn admin-btn-primary admin-btn-sm"
                              onClick={() => handleManualGrantCredit(request.id)}
                            >
                              수동입력
                            </button>
                            <button
                              type="button"
                              className="admin-btn admin-btn-danger admin-btn-sm"
                              onClick={() => handleRejectCredit(request.id)}
                            >
                              반려
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===================================================================
          TAB 3: 추천 타임라인 관리
         =================================================================== */}
      {activeTab === 'recommends' && (
        <div>
          <h3 className="admin-section-title" style={{ margin: '0 0 16px 0' }}>
            추천 타임라인 대기 신청 관리
          </h3>
          
          {pendingRecommends.length === 0 ? (
            <div className="admin-empty-state">
              <svg className="admin-empty-icon" xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
              </svg>
              <div style={{ fontWeight: 600, fontSize: '16px', color: 'var(--admin-text-main)', marginBottom: '4px' }}>대기 중인 추천 신청이 없습니다.</div>
              <div style={{ fontSize: '13px' }}>회원들이 주제영역에 대해 추천 신청을 보내면 여기에 노출됩니다.</div>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>주제영역 제목</th>
                    <th>요약 설명</th>
                    <th>신청 회원</th>
                    <th>신청 일시</th>
                    <th>상태</th>
                    <th style={{ width: '180px' }}>작업</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingRecommends.map(rec => (
                    <tr key={rec.id}>
                      <td style={{ fontWeight: 600 }}>{rec.title}</td>
                      <td style={{ fontSize: '13px', color: 'var(--admin-text-muted)' }}>{rec.summary}</td>
                      <td>{rec.requester_name || `ID: ${rec.requested_by}`}</td>
                      <td>{new Date(rec.requested_at).toLocaleString()}</td>
                      <td>
                        <span className={`admin-badge ${getRecommendBadgeClass(rec.status)}`}>
                          {rec.status}
                        </span>
                      </td>
                      <td>
                        {rec.status === 'pending' ? (
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => handleApproveRecommend(rec.id)}
                              className="admin-btn admin-btn-success admin-btn-sm"
                              style={{ padding: '6px 12px', fontSize: '12px' }}
                            >
                              승인
                            </button>
                            <button
                              onClick={() => handleRejectRecommend(rec.id)}
                              className="admin-btn admin-btn-danger admin-btn-sm"
                              style={{ padding: '6px 12px', fontSize: '12px' }}
                            >
                              거절
                            </button>
                          </div>
                        ) : (
                          <span style={{ color: 'var(--admin-text-muted)', fontSize: '12px' }}>처리 완료</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ===================================================================
          TAB 4: 시스템 & DB 관리
         =================================================================== */}
      {activeTab === 'system' && (
        <div>
          <h3 className="admin-section-title" style={{ margin: '0 0 16px 0' }}>
            시스템 데이터 초기화 패널
          </h3>
          <div className="stat-card-grid" style={{ marginBottom: '40px' }}>
            <div className="stat-card" style={{ flex: 1, flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span className="stat-icon-wrapper" style={{ width: '36px', height: '36px' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                  </svg>
                </span>
                <span style={{ fontWeight: 600, fontSize: '15px' }}>역할 마스터 데이터</span>
              </div>
              <p style={{ margin: '4px 0 12px 0', fontSize: '13px', color: 'var(--admin-text-muted)', lineHeight: '1.4' }}>
                ROLE_ADMIN, ROLE_EDITOR, ROLE_USER 역할을 강제 세팅하고 초기 정비합니다.
              </p>
              <button
                onClick={handleInitializeRoles}
                className="admin-btn admin-btn-primary admin-btn-sm"
                style={{ width: '100%' }}
              >
                역할 데이터 초기화 실행
              </button>
            </div>

            <div className="stat-card stat-success" style={{ flex: 1, flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span className="stat-icon-wrapper" style={{ width: '36px', height: '36px' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"></polyline>
                    <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"></path>
                  </svg>
                </span>
                <span style={{ fontWeight: 600, fontSize: '15px' }}>태그 시스템 초기화</span>
              </div>
              <p style={{ margin: '4px 0 12px 0', fontSize: '13px', color: 'var(--admin-text-muted)', lineHeight: '1.4' }}>
                tags, content_tags(다대다 매핑) 등 태그 마스터 테이블을 자동 스크립트로 빌드합니다.
              </p>
              <button
                onClick={handleInitializeTables}
                className="admin-btn admin-btn-success admin-btn-sm"
                style={{ width: '100%' }}
              >
                태그 테이블 초기화 실행
              </button>
            </div>
          </div>

          <h3 className="admin-section-title">
            데이터베이스 마이그레이션 관리
          </h3>
          <div className="stat-card" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '16px', marginBottom: '40px', padding: '24px', background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: 'var(--admin-text-main)' }}>마이그레이션 히스토리 및 실행</h4>
                <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--admin-text-muted)' }}>
                  서버의 SQL 마이그레이션 파일 적용 상태를 실시간 확인하고 적용합니다.
                </p>
              </div>
              <button
                onClick={handleRunMigrations}
                className="admin-btn admin-btn-primary"
                style={{ padding: '8px 16px', fontSize: '13px', fontWeight: 600 }}
              >
                미적용 마이그레이션 일괄 실행
              </button>
            </div>

            <div className="table-wrapper" style={{ marginTop: '8px', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th style={{ padding: '12px 16px' }}>마이그레이션 파일명</th>
                    <th style={{ padding: '12px 16px', width: '150px' }}>적용 상태</th>
                    <th style={{ padding: '12px 16px', width: '250px' }}>적용 일시</th>
                  </tr>
                </thead>
                <tbody>
                  {migrations.length === 0 ? (
                    <tr>
                      <td colSpan={3} style={{ textAlign: 'center', padding: '24px', color: 'var(--admin-text-muted)' }}>
                        스캔된 마이그레이션 파일이 없습니다.
                      </td>
                    </tr>
                  ) : (
                    migrations.map((mig) => (
                      <tr key={mig.name}>
                        <td style={{ fontWeight: 600, padding: '12px 16px' }}>{mig.name}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <span className={`admin-badge ${mig.applied ? 'admin-badge-success' : 'admin-badge-secondary'}`} style={{ padding: '4px 10px', fontSize: '11px', fontWeight: 600 }}>
                            {mig.applied ? '적용완료' : '미적용'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', color: mig.executedAt ? 'inherit' : 'var(--admin-text-muted)' }}>
                          {mig.executedAt ? new Date(mig.executedAt).toLocaleString() : '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ===================================================================
          MODAL 1: 신규 사용자 추가 모달
         =================================================================== */}
      {showCreateModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-card">
            <div className="admin-modal-header">
              <h3 className="admin-modal-title">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <line x1="19" y1="8" x2="19" y2="14"></line>
                  <line x1="16" y1="11" x2="22" y2="11"></line>
                </svg>
                신규 사용자 등록
              </h3>
              <button className="admin-modal-close-btn" onClick={() => setShowCreateModal(false)}>
                ✕
              </button>
            </div>
            <form onSubmit={handleCreateUser}>
              <div className="admin-modal-body">
                <div>
                  <label className="admin-label">아이디 (Username) *</label>
                  <input
                    type="text"
                    className="admin-input"
                    value={createForm.username}
                    onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
                    placeholder="사용자 아이디 입력"
                    required
                  />
                </div>
                <div>
                  <label className="admin-label">이메일 주소 *</label>
                  <input
                    type="email"
                    className="admin-input"
                    value={createForm.email}
                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                    placeholder="example@domain.com"
                    required
                  />
                </div>
                <div>
                  <label className="admin-label">비밀번호 *</label>
                  <input
                    type="password"
                    className="admin-input"
                    value={createForm.password}
                    onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                    placeholder="최소 6자 이상"
                    required
                  />
                </div>
                <div>
                  <label className="admin-label">닉네임</label>
                  <input
                    type="text"
                    className="admin-input"
                    value={createForm.nickname}
                    onChange={(e) => setCreateForm({ ...createForm, nickname: e.target.value })}
                    placeholder="표시될 사용자 닉네임"
                  />
                </div>
                <div>
                  <label className="admin-label">기본 역할 배정</label>
                  <select
                    className="admin-select"
                    value={createForm.roleName}
                    onChange={(e) => setCreateForm({ ...createForm, roleName: e.target.value })}
                  >
                    <option value="ROLE_USER">일반 사용자 (ROLE_USER)</option>
                    <option value="ROLE_EDITOR">편집자 (ROLE_EDITOR)</option>
                    <option value="ROLE_ADMIN">관리자 (ROLE_ADMIN)</option>
                  </select>
                </div>
              </div>
              <div className="admin-modal-footer">
                <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setShowCreateModal(false)}>
                  취소
                </button>
                <button type="submit" className="admin-btn admin-btn-primary">
                  등록 완료
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===================================================================
          MODAL 2: 사용자 정보 수정 모달
         =================================================================== */}
      {showEditModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-card">
            <div className="admin-modal-header">
              <h3 className="admin-modal-title">
                ✏️ 사용자 정보 수정 ({editForm.username})
              </h3>
              <button className="admin-modal-close-btn" onClick={() => setShowEditModal(false)}>
                ✕
              </button>
            </div>
            <form onSubmit={handleEditUser}>
              <div className="admin-modal-body">
                <div>
                  <label className="admin-label">이메일 주소 *</label>
                  <input
                    type="email"
                    className="admin-input"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="admin-label">닉네임</label>
                  <input
                    type="text"
                    className="admin-input"
                    value={editForm.nickname}
                    onChange={(e) => setEditForm({ ...editForm, nickname: e.target.value })}
                    placeholder="닉네임 입력"
                  />
                </div>
              </div>
              <div className="admin-modal-footer">
                <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setShowEditModal(false)}>
                  취소
                </button>
                <button type="submit" className="admin-btn admin-btn-primary">
                  저장하기
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===================================================================
          MODAL 3: 비밀번호 변경 모달
         =================================================================== */}
      {showPasswordModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-card">
            <div className="admin-modal-header">
              <h3 className="admin-modal-title">
                🔑 비밀번호 변경 ({passwordForm.username})
              </h3>
              <button className="admin-modal-close-btn" onClick={() => setShowPasswordModal(false)}>
                ✕
              </button>
            </div>
            <form onSubmit={handleResetPassword}>
              <div className="admin-modal-body">
                <div>
                  <label className="admin-label">새로운 비밀번호 *</label>
                  <input
                    type="password"
                    className="admin-input"
                    value={passwordForm.password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })}
                    placeholder="최소 6자 이상 입력"
                    required
                  />
                </div>
              </div>
              <div className="admin-modal-footer">
                <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setShowPasswordModal(false)}>
                  취소
                </button>
                <button type="submit" className="admin-btn admin-btn-danger">
                  비밀번호 변경 완료
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
